import React, { useState, useEffect } from 'react';
import { X, Clock, User, Loader2, FileText } from 'lucide-react';
import { api } from '../utils/apiClient';

const formatTimestamp = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-CO', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch {
    return iso;
  }
};

const ACTION_COLORS = {
  CREATE: 'bg-green-900/40 text-green-300 border-green-700/50',
  UPDATE: 'bg-blue-900/40 text-blue-300 border-blue-700/50',
  DELETE: 'bg-red-900/40 text-red-300 border-red-700/50',
};

const formatValue = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

const AuditLogModal = ({ isOpen, onClose, expediente }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !expediente) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await api.get(`/audit-log?expediente=${encodeURIComponent(expediente)}&limit=200`);
        if (!cancelled) setEntries(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.error || err?.message || 'Error al cargar historial');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, expediente]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-slate-900 border border-slate-700 rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-slate-800 bg-gradient-to-r from-slate-800/50 to-slate-900 rounded-t-3xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Clock size={20} className="text-blue-400" />
            <div>
              <h2 className="text-lg font-semibold text-white">Historial de cambios</h2>
              <p className="text-xs text-slate-400 mt-0.5">Expediente <span className="font-mono text-blue-300">{expediente}</span></p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-slate-700 text-slate-400 hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
          {loading && (
            <div className="flex items-center justify-center gap-2 text-slate-400 py-12">
              <Loader2 size={20} className="animate-spin" />
              <span className="text-sm">Cargando historial...</span>
            </div>
          )}
          {error && !loading && (
            <div className="rounded-2xl bg-red-900/20 border border-red-800/50 p-4 text-sm text-red-300">
              {error}
            </div>
          )}
          {!loading && !error && entries.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 rounded-2xl bg-slate-800 flex items-center justify-center mb-4">
                <FileText size={28} className="text-slate-500" />
              </div>
              <p className="text-slate-400 font-medium">Sin cambios registrados</p>
              <p className="text-slate-600 text-sm mt-1">Este expediente aún no tiene entradas de auditoría</p>
            </div>
          )}
          {!loading && !error && entries.length > 0 && (
            <div className="space-y-4">
              {entries.map((entry) => {
                const campos = entry.campos_cambiados || {};
                const campoKeys = Object.keys(campos);
                const actionClass = ACTION_COLORS[entry.action] || 'bg-slate-800 text-slate-300 border-slate-700';
                return (
                  <div key={entry.id} className="rounded-2xl border border-slate-700/50 bg-slate-800/40 p-4">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${actionClass}`}>
                          {entry.action}
                        </span>
                        <span className="text-xs text-slate-400">{entry.entity_type}</span>
                        {entry.aseguradora && (
                          <span className="text-xs text-slate-500">· {entry.aseguradora}</span>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-xs text-slate-400 flex items-center gap-1.5 justify-end">
                          <User size={12} />
                          <span>{entry.user_nickname || `user #${entry.user_id}` || '—'}</span>
                        </div>
                        <div className="text-xs text-slate-500 font-mono mt-0.5">{formatTimestamp(entry.timestamp)}</div>
                      </div>
                    </div>
                    {campoKeys.length > 0 ? (
                      <div className="rounded-xl bg-slate-900/60 border border-slate-700/40 overflow-hidden">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-slate-800/50 border-b border-slate-700/50">
                              <th className="px-3 py-2 text-left text-slate-400 font-semibold">Campo</th>
                              <th className="px-3 py-2 text-left text-slate-400 font-semibold">Antes</th>
                              <th className="px-3 py-2 text-left text-slate-400 font-semibold">Después</th>
                            </tr>
                          </thead>
                          <tbody>
                            {campoKeys.map((k) => (
                              <tr key={k} className="border-b border-slate-800 last:border-0">
                                <td className="px-3 py-2 font-mono text-slate-300">{k}</td>
                                <td className="px-3 py-2 text-red-300/80 break-all">{formatValue(campos[k]?.old)}</td>
                                <td className="px-3 py-2 text-green-300/80 break-all">{formatValue(campos[k]?.new)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-xs text-slate-500">Sin detalle de campos</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-800 bg-slate-900/60 rounded-b-3xl flex justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-xl bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium border border-slate-600 transition-all">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuditLogModal;
